import React, { useState } from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import BaseForm from 'containers/Base/BaseForm';
import {
  allowableRolesTodisplay,
  getAllowableRoleServicesMap,
  getCompanyAssetCategories,
  getSecurityCompanyServices,
} from 'reducers/securityCompany';
import {
  validationSchema,
  initialState,
  initialValues,
  generalSection,
  detailedSection,
} from './options';

import { getGeneralInfo, getUserAssignedDepartmentList } from 'reducers/users';
import { transformUserObject } from 'utils/DataTransform/utils/user';
import { isBase64 } from 'utils/checkImage';
import { changeValuesBasedOnRole } from './helper';
import staticText from 'static/text_display.json';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { EmployeeInfo } from 'utils/constants/informationText';
import { HelpLinks } from 'utils/constants/helpLinks';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';

import {
  getMultiRoleResponder,
  getSubscribedServicesByRole,
} from 'utils/helper/product';
import { ROLES } from 'utils/constants';

import { personnelGridActions, personnelSelectors } from '../';

const PersonnelForm = ({
  editMode,
  personnel,
  updatePersonnel,
  createPersonnel,
  closeForm,
  generalInfo,
  countries,
  languages,
  subscribedServices,
  userRoles,
  userAssignedDepartments,
  availableRoles,
  serviceRoleMap,
  persistentStateName,
}) => {
  const [role, setRole] = useState();
  const [isResponder, setIsResponder] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const allowableCategories = useSelector(state =>
    getCompanyAssetCategories(state),
  );
  const sites = useSites();
  const handleUserType = values => {
    const isMultiRoleResponder = getMultiRoleResponder().includes(values.role);
    values.isResponder = isMultiRoleResponder ? values.isResponder : false;
    setRole(values.role);
    setIsResponder(values.isResponder);
    setSelectedDepartments(values.departments);
  };

  const onCreate = async values => {
    let assignedSite;
    if (values.assignedSite) {
      assignedSite = find(sites, { id: values.assignedSite.id });
    }

    const dataAsPerRole = changeValuesBasedOnRole(values);

    const transformedData = transformUserObject({
      ...values,
      reliability: 5,
      assignedSite,
      qualifications: dataAsPerRole.qualifications,
      regionId: generalInfo.regionId,
      securityCompanyId: generalInfo.securityCompanyId,
      securityCompanyName: generalInfo.securityCompanyName,
    });

    await createPersonnel(transformedData);
  };

  const onEdit = async values => {
    let assignedSite;
    if (values.assignedSite) {
      assignedSite = find(sites, { id: values.assignedSite.id });
    }

    const dataAsPerRole = changeValuesBasedOnRole(values);

    const body = {
      ...values,
      assignedSite,
      qualifications: dataAsPerRole.qualifications,
      regionId: generalInfo.regionId,
      securityCompanyId: generalInfo.securityCompanyId,
      securityCompanyName: generalInfo.securityCompanyName,
    };

    if (!isBase64(values.image)) {
      Reflect.deleteProperty(body, 'image');
    }
    const formattedData = transformUserObject(body);
    const personnelId = get(personnel, 'others.id.value');

    await updatePersonnel(personnelId, formattedData);
  };

  const availableServices = getSubscribedServicesByRole({
    role,
    serviceRoleMap,
    multiRoleDisplay: { [ROLES.RESPONDER.id]: isResponder },
    subscribedServices,
  });

  let generalSectionOptions;
  if (userRoles.includes(ROLES.MANAGER.id)) {
    // remove the manager role from options
    generalSectionOptions = generalSection({
      countries,
      editMode,
      role,
      userAssignedDepartments,
      availableRoles,
      email: editMode ? personnel.generalInfo.email : '',
      securityCompanyId: generalInfo.securityCompanyId,
      selectedDepartments,
    });

    const requiredRolesToDisplay = generalSectionOptions
      .filter(f => f.field === 'role')[0]
      .options.filter(o => o.id !== ROLES.MANAGER.id);

    const roleIndex = generalSectionOptions.findIndex(
      el => el.field === 'role',
    );
    // replace existing options with requiredRolesToDisplay for manager
    generalSectionOptions[roleIndex].options = requiredRolesToDisplay;
  } else {
    generalSectionOptions = generalSection({
      countries,
      editMode,
      role,
      email: editMode ? personnel.generalInfo.email : '',
      userRoles,
      userAssignedDepartments,
      availableRoles,
      securityCompanyId: generalInfo.securityCompanyId,
      selectedDepartments,
    });
  }

  return (
    <>
      <BaseForm
        edit={editMode}
        initialValues={editMode ? initialValues(personnel) : initialState}
        formTitle={
          editMode
            ? staticText.managerial_view.personnel.edit_default
            : staticText.managerial_view.personnel.create
        }
        saveHoverText={
          editMode
            ? translatedKeyValueText('update_value_title', {
                value: personnel.generalInfo.employeeId.value,
              })
            : staticText.managerial_view.personnel.create
        }
        isImageMandatory={false}
        closeForm={closeForm}
        onSubmit={editMode ? onEdit : onCreate}
        validationSchema={validationSchema(role, editMode)}
        onChange={handleUserType}
        generalSection={generalSectionOptions}
        detailedSection={detailedSection({
          languages,
          role,
          isResponder,
          availableServices,
          allowableCategories,
          serviceRoleMap,
        })}
        hoverText={editMode ? personnel.generalInfo.employeeId.value : ''}
        note={EmployeeInfo.supervisorResponderGuardNote}
        formInfo={{
          link: HelpLinks.personnelLink,
          value: HelpLinks.personnelText,
        }}
        persistentStateName={persistentStateName}
      />
    </>
  );
};

PersonnelForm.propTypes = {
  editMode: PropTypes.bool,
  personnel: PropTypes.object,
  updatePersonnel: PropTypes.func,
  closeForm: PropTypes.func,
  countries: PropTypes.array,
  languages: PropTypes.array,
  toggleForm: PropTypes.func,
  generalInfo: PropTypes.object.isRequired,
  createPersonnel: PropTypes.func,
  userAssignedDepartments: PropTypes.array,
  services: PropTypes.array,
  subscribedServices: PropTypes.array,
  userRoles: PropTypes.array,
  availableRoles: PropTypes.array,
  serviceRoleMap: PropTypes.array,
  persistentStateName: PropTypes.string,
};

const mapStateToProps = state => ({
  personnel: personnelSelectors.getPersonnelDetailsTransformed(state),
  generalInfo: getGeneralInfo(state),
  countries: state.utils.countries,
  languages: state.utils.languages,
  subscribedServices: getSecurityCompanyServices(state),
  userRoles: state.user.general.roles,
  userAssignedDepartments: getUserAssignedDepartmentList(state),
  availableRoles: allowableRolesTodisplay(state),
  serviceRoleMap: getAllowableRoleServicesMap(state),
});

const mapDispatchToProps = dispatch => ({
  createPersonnel: personnel =>
    dispatch(personnelGridActions.createRowEntry({ personnel })),
  updatePersonnel: (id, personnel) =>
    dispatch(personnelGridActions.updateRowEntry({ id, personnel })),
});

export default connect(mapStateToProps, mapDispatchToProps)(PersonnelForm);
